/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import { Scrollbars } from 'react-custom-scrollbars';
import type { SortEnd } from 'react-sortable-hoc';
import { HSIconAutoPlay, type HSIconImage } from '@types';
import { sendWarning } from '@components/Noti';
import ImageContainer from '@components/ImageContainer';
import { Validator } from '@components/Validator';
import SortableList from '@components/SortableList';
import Tip from '@components/Tip';
import Button from '@components/Button';
import arrayMove from '@utils/arrayMove';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import Slider from '@components/Slider';
import { getMediaUrl } from '@utils/urlMakers';
import {
  $editingHotspot,
  $editingHotspotApi,
} from '@stores/editingHotspotStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import { $playerWidgetsApi } from '@stores/playerWidgets';
import { IMAGE_PLACEHOLDER } from '../../../../hotspot';
import Item from './Item';
import { selectLibImages } from '../../../../mediaLib';
import StickSetting from '../StickSetting';

const MAX = 50;

const items: DropdownItem<HSIconAutoPlay>[] = [
  { id: HSIconAutoPlay.auto, name: '图片自动轮播' },
  { id: HSIconAutoPlay.click, name: '点击图片播放下一张' },
];

function IconSettingImage() {
  const { hotspot, isBatch, batchHotspots } = useStore($editingHotspot)!;
  const icon = hotspot.icon as HSIconImage;
  const imgs = icon.media;
  const [selected, setSelected] = useState<number>(imgs.length ? 0 : -1);
  const selectedUrl = imgs[selected]?.url;

  useEffect(() => {
    return () => {
      $playerWidgetsApi.remove('icon');
    };
  }, []);

  useEffect(() => {
    if (imgs.length) {
      $playerWidgetsApi.add('icon');
    } else {
      $playerWidgetsApi.remove('icon');
    }
  }, [!imgs.length]);

  useEffect(() => {
    const urls = selectedUrl ? [getMediaUrl(selectedUrl)] : [IMAGE_PLACEHOLDER];
    if (!isBatch) {
      MainPlayer.get()?.updateImageHotspot({ id: hotspot.id, urls });
    } else {
      (batchHotspots ?? []).forEach((item) => {
        MainPlayer.get()?.updateImageHotspot({ id: item.id, urls });
      });
    }
  }, [selectedUrl]);

  const handleSelectImages = async () => {
    if (imgs.length >= MAX) {
      sendWarning(`最多支持添加${MAX}张图片`);
      return;
    }
    const { libItems } = await selectLibImages({ multiple: true });
    if (!libItems) return;
    if (libItems.length + imgs.length > MAX) {
      sendWarning('图片数超过上限，部分素材未添加成功');
      libItems.length = MAX - imgs.length;
    }
    const newImgs = libItems.map((x) => ({ id: x.id, url: x.url }));
    $editingHotspotApi.update((draft) => {
      (draft.icon as HSIconImage).media = [
        ...(draft.icon as HSIconImage).media,
        ...newImgs,
      ];
    });
    if (!imgs.length) {
      setSelected(0);
    }
  };

  const handleSortEnd = (sort: SortEnd) => {
    $editingHotspotApi.update((draft) => {
      (draft.icon as HSIconImage).media = arrayMove({
        array: (draft.icon as HSIconImage).media,
        fromIndex: sort.oldIndex,
        toIndex: sort.newIndex,
      });
    });
    if (sort.oldIndex === selected) {
      setSelected(sort.newIndex);
    } else if (sort.oldIndex > selected && sort.newIndex <= selected) {
      setSelected(selected + 1);
    } else if (sort.oldIndex < selected && sort.newIndex >= selected) {
      setSelected(selected - 1);
    }
  };

  const handleImageDelete = (index: number) => {
    $editingHotspotApi.update((draft) => {
      (draft.icon as HSIconImage).media.splice(index, 1);
    });
    if (selected > index) {
      setSelected(selected - 1);
    } else if (selected === index) {
      setSelected(imgs.length > 1 ? 0 : -1);
    }
  };

  const handlePlayTypeChange = (typeId: HSIconAutoPlay) => {
    $editingHotspotApi.update((draft) => {
      (draft.icon as HSIconImage).autoPlayType = typeId;
    });
  };

  const handleDurChange = (val: number) => {
    $editingHotspotApi.update((draft) => {
      (draft.icon as HSIconImage).autoPlayDuration = val;
    });
  };

  return (
    <>
      <Validator
        value={imgs}
        validFn={(value) => !!value.length}
        warningMessage="请选择图片"
      >
        {({ warning, value }) => (
          <ImageContainer
            warning={warning}
            className="h-155"
            placeholder={
              <div className="flex flex-col items-center">
                <p>单个图片长/宽不超过2000PX</p>
                <p>{`图片总数不超过${MAX}张`}</p>
              </div>
            }
          >
            {!!value.length && (
              <div className="h-full w-full">
                <Scrollbars>
                  <SortableList
                    axis="xy"
                    distance={10}
                    onSortEnd={handleSortEnd}
                  >
                    <div className="flex flex-wrap items-center">
                      {value.map((x, i) => (
                        <Item
                          key={i}
                          index={i}
                          listIndex={i}
                          src={x.url}
                          active={selectedUrl === x.url}
                          onClick={setSelected}
                          onDeleteClick={handleImageDelete}
                        />
                      ))}
                    </div>
                  </SortableList>
                </Scrollbars>
              </div>
            )}
          </ImageContainer>
        )}
      </Validator>
      <div className="mt-10 flex items-center justify-between">
        <Tip>单图片不超过700KB</Tip>
        <Button size="sm" onClick={handleSelectImages}>
          选择图片
        </Button>
      </div>
      {imgs.length > 1 && (
        <>
          <div className="mt-20 mb-10">交互方式：</div>
          <Dropdown
            items={items}
            selected={icon.autoPlayType}
            onChange={handlePlayTypeChange}
          />
          {icon.autoPlayType === HSIconAutoPlay.auto && (
            <Slider
              className="mt-20"
              min={1}
              max={60}
              value={icon.autoPlayDuration}
              valToString={(val) => `${val}秒/张`}
              onChange={handleDurChange}
            >
              <Slider.Header title="轮播速度：" />
              <Slider.Bar />
              <Slider.Footer />
            </Slider>
          )}
        </>
      )}
      {imgs.length > 0 && <StickSetting />}
    </>
  );
}

export default IconSettingImage;
